import { Bot, BrainCircuit, CheckCircle2, GitBranch, Package, Rocket, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

import { MermaidDiagram } from '@/components/content/MarkdownView';
import { Signature } from '@/components/Brand';
import { Badge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';

interface Project {
  code: string;
  module: string;
  title: string;
  track: string;
  trackSlug: string;
  tone: 'tech' | 'ai' | 'agent';
  icon: typeof GitBranch;
  pitch: string;
  objectives: string[];
  technologies: string[];
  steps: string[];
  deliverables: string[];
  criteria: { label: string; points: number }[];
  architecture: string;
}

const PROJECTS: Project[] = [
  {
    code: 'A12',
    module: 'Module A12',
    title: 'KH AI DevOps Assistant',
    track: 'DevOps & AI Engineering',
    trackSlug: 'devops-ai-engineering',
    tone: 'tech',
    icon: GitBranch,
    pitch:
      "Assistant d'exploitation capable d'analyser des logs, d'interroger la documentation interne par RAG et de déclencher un pipeline, avec traçabilité complète et garde-fous de sécurité. Le modèle propose, l'ingénieur décide.",
    objectives: [
      "Concevoir l'architecture de l'assistant et justifier chaque choix technique",
      'Analyser des logs et produire un diagnostic structuré validé par schéma',
      'Interroger la documentation interne par RAG avec citations vérifiables',
      'Déclencher un pipeline existant après validation humaine explicite',
      "Journaliser toutes les décisions et tous les appels pour l'audit",
      'Conteneuriser et déployer le service',
    ],
    technologies: ['Python', 'FastAPI', 'Ollama', 'Qdrant', 'Docker', 'Kubernetes', 'Jenkins', 'Prometheus'],
    steps: [
      'Cadrage : périmètre, cas d’usage prioritaires, données manipulées et classification',
      'Socle : client LLM robuste, sortie structurée validée, expurgation des secrets',
      'Analyse de logs : filtrage, regroupement, diagnostic, contrôle de cohérence',
      'RAG : indexation de la documentation, récupération, reclassement, citations',
      'Action : déclenchement de pipeline derrière une validation humaine',
      'Observabilité : journal d’audit, métriques de coût et de latence',
      'Conteneurisation, déploiement et documentation d’exploitation',
      'Soutenance : démonstration et défense des choix techniques',
    ],
    deliverables: [
      'Dépôt Git structuré avec README d’exploitation',
      'ARCHITECTURE.md avec diagrammes et justifications',
      'Service conteneurisé et manifestes de déploiement',
      'Suite de tests exécutable sans dépendance au modèle',
      'Journal d’audit exploitable et tableau de bord de coûts',
      'Support de soutenance',
    ],
    criteria: [
      { label: 'Architecture et justification des choix', points: 4 },
      { label: 'Analyse de logs : qualité et robustesse', points: 4 },
      { label: 'RAG : pertinence et citations vérifiables', points: 3 },
      { label: 'Sécurité : expurgation, validation, moindre privilège', points: 4 },
      { label: 'Tests et observabilité', points: 3 },
      { label: 'Documentation et soutenance', points: 2 },
    ],
    architecture: `graph LR
    OPS["Ingénieur<br/>d'astreinte"] --> UI["Interface<br/>FastAPI"]
    UI --> ORCH["Orchestrateur<br/>d'assistant"]
    ORCH --> RED["Expurgation<br/>secrets, IP, PII"]
    RED --> LLM["LLM local<br/>Ollama"]
    ORCH --> RAG["Récupération<br/>Qdrant"]
    RAG --> DOCS[("Documentation<br/>interne")]
    ORCH --> VAL["Validation<br/>de schéma"]
    VAL --> HUMAN{"Validation<br/>humaine"}
    HUMAN -->|approuvé| CI["Pipeline<br/>Jenkins"]
    HUMAN -->|refusé| AUDIT
    ORCH --> AUDIT[("Journal<br/>d'audit")]
    ORCH --> METRICS["Prometheus<br/>coût, latence"]`,
  },
  {
    code: 'B10',
    module: 'Module B10',
    title: 'KH MLOps Platform',
    track: 'MLOps & AI Platform Engineering',
    trackSlug: 'mlops-ai-platform-engineering',
    tone: 'ai',
    icon: BrainCircuit,
    pitch:
      "Plateforme MLOps complète et exploitable : ingestion versionnée, pipeline d'entraînement orchestré, registre de modèles, service d'inférence sur Kubernetes, surveillance de dérive et promotion GitOps.",
    objectives: [
      'Versionner données et modèles de façon reproductible',
      "Orchestrer un pipeline d'entraînement de bout en bout",
      'Suivre et comparer les expériences, puis promouvoir un modèle',
      "Servir le modèle sur Kubernetes avec autoscaling",
      'Surveiller la dérive et déclencher un réentraînement sur critère objectif',
      'Automatiser la promotion entre environnements par GitOps',
    ],
    technologies: ['Python', 'DVC', 'MinIO', 'Airflow', 'MLflow', 'KServe', 'Evidently', 'Argo CD', 'Kubernetes'],
    steps: [
      'Ingestion : dataset versionné sur MinIO avec lignage documenté',
      'Pipeline Airflow : préparation, entraînement, évaluation, enregistrement',
      'MLflow : suivi des expériences et registre de modèles',
      'Service : déploiement KServe avec ressources et sondes',
      'Surveillance : rapports Evidently, seuils et alertes',
      'Boucle : réentraînement déclenché par la dérive',
      'GitOps : promotion contrôlée et retour arrière testé',
      'Soutenance : démonstration du cycle complet',
    ],
    deliverables: [
      'Dépôt applicatif et dépôt GitOps séparés',
      'DAG Airflow et composants réutilisables',
      'Modèle enregistré et promu dans MLflow',
      'Manifestes Helm du service d’inférence',
      'Rapports de dérive et règles d’alerte',
      'Procédure de retour arrière vérifiée',
    ],
    criteria: [
      { label: 'Reproductibilité : données et modèles versionnés', points: 4 },
      { label: 'Orchestration : pipeline fiable et idempotent', points: 4 },
      { label: 'Service : déploiement et dimensionnement', points: 3 },
      { label: 'Surveillance : dérive et alertes exploitables', points: 4 },
      { label: 'GitOps et retour arrière', points: 3 },
      { label: 'Documentation et soutenance', points: 2 },
    ],
    architecture: `graph TB
    SRC[("Source<br/>de données")] --> DVC["DVC<br/>versionnage"]
    DVC --> MINIO[("MinIO<br/>stockage objet")]
    MINIO --> AF["Airflow<br/>pipeline"]
    AF --> TRAIN["Entraînement<br/>+ évaluation"]
    TRAIN --> MLF["MLflow<br/>registre"]
    MLF -->|promotion| GITOPS["Dépôt<br/>GitOps"]
    GITOPS --> ARGO["Argo CD"]
    ARGO --> KSERVE["KServe<br/>InferenceService"]
    KSERVE --> CLIENT["Applications<br/>clientes"]
    KSERVE --> EVID["Evidently<br/>dérive"]
    EVID -->|seuil dépassé| AF`,
  },
  {
    code: 'C06',
    module: 'Module C06',
    title: 'KH Multi-Agent DevSecOps Platform',
    track: 'AI Agents Engineering',
    trackSlug: 'ai-agents-engineering',
    tone: 'agent',
    icon: Bot,
    pitch:
      "Système multi-agents qui analyse un dépôt, détecte des vulnérabilités, propose un correctif, ouvre une demande de fusion et soumet toute action sensible à une approbation humaine. Chaque agent est cloisonné et audité.",
    objectives: [
      'Définir rôles, objectifs et outils de chaque agent',
      'Orchestrer la délégation et arbitrer les conflits',
      'Exposer les outils via MCP avec contrôle des droits',
      'Cloisonner chaque agent selon le moindre privilège',
      'Soumettre toute action à effet de bord à une approbation humaine',
      'Superviser coûts, latence et taux d’échec, et documenter l’arrêt d’urgence',
    ],
    technologies: ['Python', 'LangGraph', 'CrewAI', 'MCP', 'Trivy', 'Gitleaks', 'Docker', 'Kubernetes'],
    steps: [
      'Conception : rôles des agents, protocole d’échange, critères d’arrêt',
      'Outils : serveur MCP exposant analyse, lecture de dépôt et création de MR',
      'Agent analyste : cartographie du dépôt et des dépendances',
      'Agent sécurité : Trivy et Gitleaks, qualification des résultats',
      'Agent correcteur : proposition de correctif et tests',
      'Agent relecteur : arbitrage et ouverture de la demande de fusion',
      'Approbation humaine, journal d’audit et supervision',
      'Déploiement cloisonné et soutenance',
    ],
    deliverables: [
      'Code des agents et du serveur MCP',
      'Graphe d’orchestration documenté',
      'Démonstration sur un dépôt vulnérable de test',
      'Journal d’audit des décisions de chaque agent',
      'Matrice des droits par agent',
      'Procédure d’arrêt d’urgence',
    ],
    criteria: [
      { label: 'Conception multi-agents et orchestration', points: 4 },
      { label: 'Outils MCP et interopérabilité', points: 3 },
      { label: 'Détection de vulnérabilités et qualification', points: 3 },
      { label: 'Cloisonnement et moindre privilège', points: 4 },
      { label: 'Approbation humaine et auditabilité', points: 4 },
      { label: 'Documentation et soutenance', points: 2 },
    ],
    architecture: `graph TB
    REPO[("Dépôt<br/>Git")] --> SUP["Agent superviseur<br/>LangGraph"]
    SUP --> A1["Agent analyste<br/>cartographie"]
    SUP --> A2["Agent sécurité<br/>Trivy, Gitleaks"]
    SUP --> A3["Agent correcteur<br/>patch + tests"]
    SUP --> A4["Agent relecteur<br/>arbitrage"]
    A1 --> MCP["Serveur MCP<br/>outils typés"]
    A2 --> MCP
    A3 --> MCP
    MCP --> RBAC{"Matrice<br/>de droits"}
    A4 --> GATE{"Approbation<br/>humaine"}
    GATE -->|approuvé| MR["Demande<br/>de fusion"]
    GATE -->|refusé| LOG
    SUP --> LOG[("Journal<br/>d'audit")]`,
  },
];

const TONE_CLASSES = {
  tech: { text: 'text-tech', bg: 'bg-tech/10', ring: 'ring-tech/25' },
  ai: { text: 'text-ai', bg: 'bg-ai/10', ring: 'ring-ai/25' },
  agent: { text: 'text-agent', bg: 'bg-agent/10', ring: 'ring-agent/25' },
} as const;

export function ProjectsPage() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <header className="max-w-3xl">
        <Badge tone="ai" className="mb-4">
          <Rocket className="h-3 w-3" />
          Projets fil rouge
        </Badge>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Projets professionnels</h1>
        <p className="mt-4 text-muted">
          Chaque parcours se conclut par un projet complet, réalisé en autonomie, soutenu devant le
          formateur et évalué sur une grille de 20 points. Ce ne sont pas des exercices : ce sont des
          systèmes déployables.
        </p>
      </header>

      <div className="mt-14 space-y-16">
        {PROJECTS.map((project) => {
          const tone = TONE_CLASSES[project.tone];
          const Icon = project.icon;
          const total = project.criteria.reduce((sum, item) => sum + item.points, 0);
          return (
            <section key={project.code} aria-labelledby={`projet-${project.code}`}>
              <div className="flex flex-wrap items-start gap-4">
                <span
                  className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${tone.bg} ring-1 ring-inset ${tone.ring}`}
                >
                  <Icon className={`h-6 w-6 ${tone.text}`} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={project.tone}>{project.code}</Badge>
                    <Link
                      to={`/parcours/${project.trackSlug}`}
                      className="text-xs font-medium text-muted hover:text-fg hover:underline"
                    >
                      {project.track}
                    </Link>
                  </div>
                  <h2 id={`projet-${project.code}`} className="mt-2 text-2xl font-bold tracking-tight">
                    {project.title}
                  </h2>
                  <p className="mt-3 max-w-3xl text-muted">{project.pitch}</p>
                </div>
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle as="h3">Architecture</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <MermaidDiagram definition={project.architecture} />
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle as="h3" className="flex items-center gap-2">
                      <Target className={`h-4 w-4 ${tone.text}`} />
                      Objectifs
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <ul className="space-y-2">
                      {project.objectives.map((objective) => (
                        <li key={objective} className="flex gap-2 text-sm text-muted">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-hidden="true" />
                          {objective}
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle as="h3">Étapes de réalisation</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <ol className="space-y-2">
                      {project.steps.map((step, index) => (
                        <li key={step} className="flex gap-2.5 text-sm text-muted">
                          <span
                            className={`shrink-0 font-mono text-xs font-bold ${tone.text}`}
                            aria-hidden="true"
                          >
                            {index + 1}.
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle as="h3" className="flex items-center gap-2">
                      <Package className={`h-4 w-4 ${tone.text}`} />
                      Livrables
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <ul className="ml-4 list-disc space-y-1.5 text-sm text-muted">
                      {project.deliverables.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle as="h3">Critères d'évaluation</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <ul className="space-y-2">
                      {project.criteria.map((criterion) => (
                        <li
                          key={criterion.label}
                          className="flex items-start justify-between gap-3 text-sm"
                        >
                          <span className="text-muted">{criterion.label}</span>
                          <span className="shrink-0 font-semibold tabular-nums text-fg">
                            {criterion.points}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 border-t border-border pt-3 text-right text-sm font-bold">
                      Total : {total} points
                    </p>
                  </CardBody>
                </Card>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <ButtonLink to={`/parcours/${project.trackSlug}`} variant="outline" size="sm">
                  Voir le parcours {project.track}
                </ButtonLink>
                <ButtonLink to={`/modules/${project.code}`} variant="ghost" size="sm">
                  Fiche du {project.module}
                </ButtonLink>
              </div>
            </section>
          );
        })}
      </div>

      <Signature className="mt-20 text-center" />
    </div>
  );
}
